import {
  Box,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  TextField,
} from "@mui/material";
import { MultipleUserPicker, type UserOption } from "components/MultipleUserPicker";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  type CredentialImplementationForStaffQuery,
  ImplementationStatus,
} from "generated-graphql/graphql";

import { DrawerSection, drawerSectionStackSpacing, drawerTwoColumnGridSx } from "./DrawerSection";

type Row = NonNullable<CredentialImplementationForStaffQuery["credentialImplementationForStaff"]>;

const STATUSES = [
  ImplementationStatus.NotStarted,
  ImplementationStatus.InProgress,
  ImplementationStatus.Blocked,
  ImplementationStatus.Complete,
  ImplementationStatus.NotApplicable,
];

export type EditableFormState = {
  implementationStatus: ImplementationStatus;
  assignee: UserOption | null;
  notes: string;
  blockedReason: string;
};

export function EditableFields({
  form,
  onChange,
}: {
  form: EditableFormState;
  onChange: (next: EditableFormState) => void;
}) {
  const handleStatus = (e: SelectChangeEvent<ImplementationStatus>) => {
    onChange(applyStatusChange(form, e.target.value as ImplementationStatus));
  };

  const assigneeValue: UserOption[] | null = form.assignee ? [form.assignee] : null;

  return (
    <Stack spacing={drawerSectionStackSpacing} divider={<Divider flexItem />}>
      <DrawerSection title="Status & assignment">
        <Stack spacing={2}>
          <Box sx={{ ...drawerTwoColumnGridSx, alignItems: "stretch" }}>
            <FormControl fullWidth size="small" variant="outlined" sx={{ minWidth: 0 }}>
              <InputLabel id="ci-implementation-status-label">Implementation status</InputLabel>
              <Select<ImplementationStatus>
                labelId="ci-implementation-status-label"
                label="Implementation status"
                value={form.implementationStatus}
                onChange={handleStatus}
              >
                {STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {prettyPrintEnumValue(s, [], true)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ minWidth: 0 }}>
              <MultipleUserPicker
                staffOnly
                multiple={false}
                label="Assignee"
                value={assigneeValue}
                onChange={(users) => onChange({ ...form, assignee: users?.[0] ?? null })}
              />
            </Box>
          </Box>
          {form.implementationStatus === ImplementationStatus.Blocked ? (
            <TextField
              label="Blocked reason"
              value={form.blockedReason}
              onChange={(e) => onChange({ ...form, blockedReason: e.target.value })}
              multiline
              minRows={2}
              fullWidth
              size="small"
              variant="outlined"
              required
              helperText="Required when status is Blocked"
            />
          ) : null}
        </Stack>
      </DrawerSection>

      <DrawerSection title="Notes">
        <TextField
          value={form.notes}
          onChange={(e) => onChange({ ...form, notes: e.target.value })}
          multiline
          minRows={3}
          fullWidth
          size="small"
          placeholder="Notes"
          helperText="Pinned context for this facility (separate from the comment thread below)."
        />
      </DrawerSection>
    </Stack>
  );
}

/**
 * Apply a status transition to the form. Mirrors the server rule: when status
 * is not BLOCKED, blockedReason is wiped (the server ignores it anyway). When
 * status becomes BLOCKED we keep whatever the user had typed so toggling back
 * and forth doesn't lose work.
 */
export function applyStatusChange(
  form: EditableFormState,
  implementationStatus: ImplementationStatus
): EditableFormState {
  return {
    ...form,
    implementationStatus,
    blockedReason: implementationStatus === ImplementationStatus.Blocked ? form.blockedReason : "",
  };
}

export function rowToFormState(row: Row): EditableFormState {
  return {
    implementationStatus: row.implementationStatus,
    assignee:
      row.assigneeId && row.assigneeEmail
        ? {
            id: row.assigneeId,
            email: row.assigneeEmail,
            name: row.assigneeName ?? row.assigneeEmail,
          }
        : null,
    notes: row.notes ?? "",
    blockedReason: row.blockedReason ?? "",
  };
}
